import { ExportDelivery, type ExportArchive, type ExportOptions, type ExportPlan, type ExportResult } from '@/core/export/types'
import type { Document, SourceId } from '@/core/document'
import type { Point, Rect, Size } from '@/core/geometry'

export enum ExportMessageType {
  Request = 'request',
  Success = 'success',
  Failure = 'failure',
  RenderForQuality = 'render-for-quality',
  QualityRendered = 'quality-rendered',
  EncodeQualityWindow = 'encode-quality-window',
  QualityWindowEncoded = 'quality-window-encoded',
  EncodeQualityFull = 'encode-quality-full',
  QualityFullEncoded = 'quality-full-encoded',
  DisposeQuality = 'dispose-quality',
  QualityDisposed = 'quality-disposed',
}

export interface ExportRequestSource {
  id: SourceId
  bitmap: ImageBitmap
  width: number
  height: number
}

export interface ExportRequest {
  type: ExportMessageType.Request
  sources: ExportRequestSource[]
  document: Document
  fileName: string
  plan: ExportPlan
  options: ExportOptions
  delivery: ExportDelivery
  maxCanvasArea: number
}

export interface ExportSuccessResponse {
  type: ExportMessageType.Success
  results: ExportResult[]
  archive?: ExportArchive
}

export interface ExportFailureResponse {
  type: ExportMessageType.Failure
  message: string
  requestId?: number
}

export interface RenderForQualityRequest {
  type: ExportMessageType.RenderForQuality
  requestId: number
  sources: ExportRequestSource[]
  document: Document
  plan: ExportPlan
  options: ExportOptions
  maxCanvasArea: number
}

export interface QualityRenderedResponse {
  type: ExportMessageType.QualityRendered
  requestId: number
  navigator: ImageBitmap
  outputSize: Size
}

export interface EncodeQualityWindowRequest {
  type: ExportMessageType.EncodeQualityWindow
  requestId: number
  point: Point
  quality: number
}

export interface QualityWindowEncodedResponse {
  type: ExportMessageType.QualityWindowEncoded
  requestId: number
  rect: Rect
  original: ImageBitmap
  jpeg: ImageBitmap
  difference: ImageBitmap
  bytes: number
}

export interface EncodeQualityFullRequest {
  type: ExportMessageType.EncodeQualityFull
  requestId: number
  quality: number
}

export interface QualityFullEncodedResponse {
  type: ExportMessageType.QualityFullEncoded
  requestId: number
  jpegBytes: number
  pngBytes: number
}

export interface DisposeQualityRequest {
  type: ExportMessageType.DisposeQuality
  requestId: number
}

export interface QualityDisposedResponse {
  type: ExportMessageType.QualityDisposed
  requestId: number
}

export type QualityRequest =
  | RenderForQualityRequest
  | EncodeQualityWindowRequest
  | EncodeQualityFullRequest
  | DisposeQualityRequest

export type QualityResponse =
  | QualityRenderedResponse
  | QualityWindowEncodedResponse
  | QualityFullEncodedResponse
  | QualityDisposedResponse
  | ExportFailureResponse

export type ExportWorkerRequest = ExportRequest | QualityRequest
export type ExportResponse = ExportSuccessResponse | ExportFailureResponse
export type ExportWorkerResponse = ExportResponse | QualityResponse
